/**
 * LocalStorage utilities for caching planner data
 * 
 * Provides "offline-ish" support by caching planner snapshots in localStorage.
 * When the network is unavailable or slow, the app can show the last-known state.
 * 
 * CONVENTION:
 * - All planner data is stored under user-scoped, namespaced keys (e.g., 'planner:{userId}:farms')
 * - Data is serialized as JSON
 * - Functions handle serialization/deserialization and error handling
 * - Cache keys MUST include userId to prevent cross-account data leakage
 */

/**
 * Save a planner snapshot to localStorage
 * 
 * @param snapshotKey - Namespaced key for the snapshot (e.g., 'planner:{userId}:farms')
 * @param data - Data to save (will be JSON serialized)
 */
export function savePlannerSnapshot(snapshotKey: string, data: unknown): void {
  try {
    const serialized = JSON.stringify(data);
    localStorage.setItem(snapshotKey, serialized);
  } catch (err) {
    // Silently fail if localStorage is unavailable or quota exceeded
    // We don't want to break the app if caching fails
    console.warn(`Failed to save planner snapshot for key "${snapshotKey}":`, err);
  }
}

/**
 * Load a planner snapshot from localStorage
 * 
 * @param snapshotKey - Namespaced key for the snapshot (e.g., 'planner:{userId}:farms')
 * @returns Parsed data from localStorage, or null if not found or invalid
 */
export function loadPlannerSnapshot<T>(snapshotKey: string): T | null {
  try {
    const serialized = localStorage.getItem(snapshotKey);
    if (!serialized) {
      return null;
    }
    return JSON.parse(serialized) as T;
  } catch (err) {
    // Return null if parsing fails or localStorage is unavailable
    console.warn(`Failed to load planner snapshot for key "${snapshotKey}":`, err);
    return null;
  }
}

/**
 * Clear a specific planner snapshot from localStorage
 * 
 * @param snapshotKey - Namespaced key for the snapshot to clear
 */
export function clearPlannerSnapshot(snapshotKey: string): void {
  try {
    localStorage.removeItem(snapshotKey);
  } catch (err) {
    console.warn(`Failed to clear planner snapshot for key "${snapshotKey}":`, err);
  }
}

/**
 * Clear all planner snapshots from localStorage
 * Useful for logout or data reset scenarios
 */
export function clearAllPlannerSnapshots(): void {
  try {
    const keys = Object.keys(localStorage);
    const plannerKeys = keys.filter(key => key.startsWith('planner:'));
    plannerKeys.forEach(key => localStorage.removeItem(key));
  } catch (err) {
    console.warn('Failed to clear all planner snapshots:', err);
  }
}

/**
 * Get a user-scoped cache key for planner data
 * 
 * @param userId - Stable user identifier (e.g., from auth token subject/ID)
 * @param entityType - Type of entity (e.g., 'farms', 'fields', 'plantings', 'todos')
 * @param suffix - Optional suffix for additional scoping (e.g., farmId, date)
 * @returns User-scoped cache key (e.g., 'planner:{userId}:farms')
 */
export function getPlannerCacheKey(userId: string, entityType: string, suffix?: string): string {
  const parts = ['planner', userId, entityType];
  if (suffix) {
    parts.push(suffix);
  }
  return parts.join(':');
}

